import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for scroll-triggered animations using Intersection Observer
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Percentage of element visible before triggering (0-1)
 * @param {string} options.rootMargin - Margin around root (e.g., '0px 0px -100px 0px')
 * @param {boolean} options.triggerOnce - Whether to trigger animation only once
 * @returns {Object} - { ref, isVisible, hasAnimated }
 */
export const useScrollAnimation = (options = {}) => {
  const {
    threshold = 0.1,
    rootMargin = '0px 0px -50px 0px',
    triggerOnce = true,
  } = options;

  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Check if user prefers reduced motion
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (prefersReducedMotion) {
      setIsVisible(true);
      setHasAnimated(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Trigger immediately if element is already in view on mount
        if (entry.isIntersecting || entry.intersectionRatio > 0) {
          setIsVisible(true);
          if (triggerOnce) {
            setHasAnimated(true);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      {
        threshold,
        rootMargin,
      }
    );

    observer.observe(element);

    // Check if element is already visible on mount
    setTimeout(() => {
      if (element) {
        const rect = element.getBoundingClientRect();
        const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
        if (isInViewport) {
          setIsVisible(true);
          if (triggerOnce) {
            setHasAnimated(true);
          }
        }
      }
    }, 100);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [threshold, rootMargin, triggerOnce]);

  return { ref, isVisible, hasAnimated };
};

/**
 * Hook for staggered animations (e.g., grid items appearing one after another)
 * @param {number} itemCount - Number of items to animate
 * @param {number} staggerDelay - Delay between each item (in ms)
 * @returns {Object} - { ref, getItemStyle }
 */
export const useStaggerAnimation = (itemCount, staggerDelay = 100) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.05 });

  const getItemStyle = (index) => ({
    animationDelay: isVisible ? `${index * staggerDelay}ms` : '0ms',
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
  });

  return { ref, getItemStyle, isVisible };
};

export default useScrollAnimation;
